import { useState, useEffect, useMemo } from "react";
import {
  Search,
  Plus,
  Phone,
  Mail,
  Home,
  Award,
  Briefcase,
  Hash,
  DoorOpen,
  Laptop,
  Truck,
  User,
  Shield,
  Clock,
  Edit2,
  Eye,
  AlertTriangle,
  UserX,
  PhoneCall,
  Trash2,
  Lock,
  Upload,
  Download,
  FileSpreadsheet,
} from "lucide-react";
import Modal from "../ui/Modal";
import Select from "../ui/Select";
import FormField from "../ui/FormField";
import Input from "../ui/Input";
import TablePagination from "../common/TablePagination";

export const STANDARD_DESIGNATIONS = [
  "Director",
  "Head of Department (HOD)",
  "General Manager (GM)",
  "Operations Lead",
  "Team Lead",
  "Senior Manager",
  "Manager",
  "Assistant Manager",
  "Senior Executive",
  "Executive",
  "Junior Executive",
  "Senior Specialist / Analyst",
  "Specialist / Analyst",
  "Associate",
  "Coordinator",
  "Officer",
  "Consultant",
  "Floor Receptionist",
  "Office Assistant",
  "Intern / Trainee",
  "Other (Custom Designation)",
];
import {
  getSocietyResidents,
  getSocietyTenants,
  getSocietyVehicles,
  getSocietyPets,
  getSocietyCommittee,
  getEmployeeFormData,
  createSocietyVehicle,
  createSocietyTenant,
  createSocietyResident,
  updateSocietyResident,
  revokeSocietyResident,
  assignCommitteeRole,
  revokeCommitteeRole,
  getSocietyHotlines,
  saveSocietyHotline,
  deleteSocietyHotline,
  exportSocietyResidents,
  downloadEmployeeTemplate,
  importSocietyResidents,
} from "../../services/societyService";
import {
  normalizePropertyType,
  PROPERTY_CATEGORIES,
  getPropertyLeadershipConfig,
} from "../../utils/propertyStructure";
import { getCurrentRoleKey } from "../../services/authService";
import masterService from "../../services/masterEntitiesService";

const PropertyDirectories = ({ societyId, propertyType }) => {
  const normalizedCategory = normalizePropertyType(propertyType);
  const isIndustrial = normalizedCategory === PROPERTY_CATEGORIES.INDUSTRIAL;
  const isCommercial = normalizedCategory === PROPERTY_CATEGORIES.COMMERCIAL;

  const roleKey = getCurrentRoleKey();
  const canManage =
    roleKey === "super-admin" || roleKey === "admin" || roleKey === "society-admin";

  const leadershipConfig = useMemo(
    () => getPropertyLeadershipConfig(propertyType),
    [propertyType]
  );

  const defaultTab = isCommercial ? "tenants" : "residents";
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);

  const [data, setData] = useState({
    residents: [],
    tenants: [],
    vehicles: [],
    pets: [],
    management: [],
    contractors: [],
    hotlines: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form metadata options (departments, floors, managers)
  const [formMeta, setFormMeta] = useState({
    departments: [],
    floors: [],
    managers: [],
    departmentFloors: {},
    floorDepartments: {},
    desks: [],
    propertyName: "",
  });

  // 1. Add Employee Modal
  const [isAddEmployeeOpen, setIsAddEmployeeOpen] = useState(false);
  const [isImportEmployeeOpen, setIsImportEmployeeOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [employeeForm, setEmployeeForm] = useState({
    name: "",
    phone: "",
    email: "",
    department: "",
    floor: "",
    designation: "",
    is_custom_designation: false,
    reports_to_user_id: "",
    seating_type: "DESK", // "DESK" or "CABIN"
    seating_assignment_mode: "AUTO", // "AUTO" (generate new) or "EXISTING" (assign pre-existing)
    selected_existing_seat: "",
    desk_code: "",
    cabin_code: "",
    extension: "",
    is_delegator: true,
    is_delegatee: true,
  });

  // 1b. Hotlines Management Modal
  const [isHotlineModalOpen, setIsHotlineModalOpen] = useState(false);
  const [hotlineModalMode, setHotlineModalMode] = useState("add"); // "add" | "edit"
  const [hotlineForm, setHotlineForm] = useState({
    id: "",
    title: "",
    subtitle: "",
    contact_number: "",
    extension: "",
    category: "GENERAL",
    is_mandatory: false,
  });
  const [hotlineDeleteTarget, setHotlineDeleteTarget] = useState(null);

  // 2. Add HOD / Assign Leadership Modal
  const [isAssignHODOpen, setIsAssignHODOpen] = useState(false);
  const [assignHODData, setAssignHODData] = useState({
    residentId: "",
    role: leadershipConfig.roles[0]?.value || "Department Head",
    deskCode: "",
    cabinCode: "",
  });

  // 3. Edit HOD Modal
  const [memberToEdit, setMemberToEdit] = useState(null);
  const [editHODData, setEditHODData] = useState({
    role: "",
    deskCode: "",
    cabinCode: "",
  });

  // 3b. View Member / Colleague Modal
  const [viewMember, setViewMember] = useState(null);

  // 3c. Edit Employee Modal
  const [editEmployee, setEditEmployee] = useState(null);

  // 3d. Revoke Confirmation Modal
  const [revokeTarget, setRevokeTarget] = useState(null); // { id, name, type: "management" | "resident", expectedDeskCode }
  const [revokeDeskCodeInput, setRevokeDeskCodeInput] = useState("");
  const [revokeError, setRevokeError] = useState("");

  // 4. Add Vehicle Modal
  const [isAddVehicleOpen, setIsAddVehicleOpen] = useState(false);
  const [vehicleForm, setVehicleForm] = useState({
    registration_number: "",
    vehicle_type: "Car",
    model: "",
    slot_code: "",
  });

  // 5. Add Contractor / Tenant Modal
  const [isAddContractorOpen, setIsAddContractorOpen] = useState(false);
  const [contractorForm, setContractorForm] = useState({
    name: "",
    vendor_name: "",
    contact_number: "",
    area: "",
    contract_end: "",
  });

  useEffect(() => {
    if (societyId) {
      fetchTabData(activeTab);
      loadEmployeeFormMeta();
    }
  }, [societyId, activeTab]);

  const loadEmployeeFormMeta = async () => {
    try {
      const [meta, masterDepts] = await Promise.all([
        getEmployeeFormData(societyId).catch(() => null),
        masterService.getMasterDepartmentTypes({ property_id: societyId, active_only: "true" }).catch(() => [])
      ]);

      const deptsMap = new Map();
      if (meta?.departments && Array.isArray(meta.departments)) {
        meta.departments.forEach((d) => {
          if (d?.name) deptsMap.set(d.name.toLowerCase(), d);
        });
      }
      if (Array.isArray(masterDepts)) {
        masterDepts.forEach((md) => {
          if (md?.name) {
            const key = md.name.toLowerCase();
            if (!deptsMap.has(key)) {
              deptsMap.set(key, { id: md.id, name: md.name, floor: null, head_user_id: null });
            }
          }
        });
      }

      setFormMeta({
        departments: Array.from(deptsMap.values()).sort((a, b) => a.name.localeCompare(b.name)),
        floors: meta?.floors || [],
        managers: meta?.managers || [],
        departmentFloors: meta?.departmentFloors || {},
        floorDepartments: meta?.floorDepartments || {},
        desks: meta?.desks || [],
        propertyName: meta?.property_name || "",
      });
    } catch (err) {
      console.error("Error loading employee form meta:", err);
    }
  };

  const fetchTabData = async (tab) => {
    setLoading(true);
    let result = [];
    try {
      if (tab === "residents") result = await getSocietyResidents(societyId);
      else if (tab === "tenants") result = await getSocietyTenants(societyId);
      else if (tab === "vehicles") result = await getSocietyVehicles(societyId);
      else if (tab === "pets") result = await getSocietyPets(societyId);
      else if (tab === "management") result = await getSocietyCommittee(societyId);
      else if (tab === "contractors") result = await getSocietyTenants(societyId);
      else if (tab === "hotlines") result = await getSocietyHotlines(societyId);

      const parsedData = Array.isArray(result) ? result : result?.data || [];
      setData((prev) => ({ ...prev, [tab]: parsedData }));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-generation helper for Desk Code, Cabin Code, and Intercom Extension
  const autoGenerateCodes = (updates) => {
    const next = { ...employeeForm, ...updates };

    if (next.seating_assignment_mode !== "EXISTING") {
      const existingDeskCodes = [
        ...(formMeta.desks || []).map((d) => d.flat_number),
        ...(data.residents || []).flatMap((r) => [r.desk_code, r.cabin_code]).filter(Boolean),
      ];

      const rawFloor = String(next.floor ?? "0");
      const floorNum = rawFloor.toLowerCase().includes("ground") || rawFloor === "0" ? "0" : rawFloor.replace(/\D/g, "") || "0";
      const flPrefix = floorNum === "0" ? "G" : floorNum;

      if (next.seating_type === "CABIN") {
        const cabinRegex = new RegExp(`^C-${flPrefix}(\\d+)`, "i");
        const existingCabNums = existingDeskCodes
          .map((c) => {
            const m = String(c).match(cabinRegex);
            return m ? parseInt(m[1], 10) : 0;
          })
          .filter((n) => n > 0);
        const nextCabNum = existingCabNums.length > 0 ? Math.max(...existingCabNums) + 1 : 1;
        const cabCode = `C-${flPrefix}${String(nextCabNum).padStart(2, "0")}`;
        next.cabin_code = cabCode;
        next.desk_code = `${cabCode}-D1`;
      } else {
        const deskRegex = new RegExp(`^D-${flPrefix}(\\d+)(?:-S(\\d+))?`, "i");
        const existingDeskNums = existingDeskCodes
          .map((c) => {
            const m = String(c).match(deskRegex);
            return m ? parseInt(m[1], 10) : 0;
          })
          .filter((n) => n > 0);
        const nextDeskNum = existingDeskNums.length > 0 ? Math.max(...existingDeskNums) + 1 : 1;
        next.cabin_code = "";
        next.desk_code = `D-${flPrefix}${String(nextDeskNum).padStart(2, "0")}-S1`;
      }
    }

    setEmployeeForm(next);
  };

  // Auto-generation helper for Edit Employee modal
  const autoGenerateEditCodes = (updates) => {
    if (!editEmployee) return;
    const next = { ...editEmployee, ...updates };

    if (next.seating_assignment_mode !== "EXISTING") {
      const existingDeskCodes = [
        ...(formMeta.desks || []).map((d) => d.flat_number),
        ...(data.residents || []).flatMap((r) => [r.desk_code, r.cabin_code]).filter(Boolean),
      ];

      const rawFloor = String(next.floor ?? "0");
      const floorNum = rawFloor.toLowerCase().includes("ground") || rawFloor === "0" ? "0" : rawFloor.replace(/\D/g, "") || "0";
      const flPrefix = floorNum === "0" ? "G" : floorNum;

      if (next.seating_type === "CABIN") {
        const cabinRegex = new RegExp(`^C-${flPrefix}(\\d+)`, "i");
        const existingCabNums = existingDeskCodes
          .map((c) => {
            const m = String(c).match(cabinRegex);
            return m ? parseInt(m[1], 10) : 0;
          })
          .filter((n) => n > 0);
        const nextCabNum = existingCabNums.length > 0 ? Math.max(...existingCabNums) + 1 : 1;
        const cabCode = `C-${flPrefix}${String(nextCabNum).padStart(2, "0")}`;
        next.cabin_code = cabCode;
        next.desk_code = `${cabCode}-D1`;
      } else {
        const deskRegex = new RegExp(`^D-${flPrefix}(\\d+)(?:-S(\\d+))?`, "i");
        const existingDeskNums = existingDeskCodes
          .map((c) => {
            const m = String(c).match(deskRegex);
            return m ? parseInt(m[1], 10) : 0;
          })
          .filter((n) => n > 0);
        const nextDeskNum = existingDeskNums.length > 0 ? Math.max(...existingDeskNums) + 1 : 1;
        next.cabin_code = "";
        next.desk_code = `D-${flPrefix}${String(nextDeskNum).padStart(2, "0")}-S1`;
      }
    }

    setEditEmployee(next);
  };

  // Standard & Custom Designation Dropdown Options
  const designationOptions = useMemo(() => {
    return STANDARD_DESIGNATIONS.map((title) => ({
      value: title,
      label: title,
    }));
  }, []);

  // 1. Linked Departments based on selected Floor (Add Employee)
  const addDepartmentOptions = useMemo(() => {
    if (employeeForm.floor !== "" && employeeForm.floor !== null && employeeForm.floor !== undefined) {
      const flKey = String(employeeForm.floor);
      const deptsOnThisFloor = (formMeta.floorDepartments?.[flKey] || []).map((d) => d.toLowerCase());
      return formMeta.departments.filter((dept) => {
        const dKey = (dept.name || "").toLowerCase();
        const deptFloors = formMeta.departmentFloors?.[dKey] || dept.floors || [];
        if (deptsOnThisFloor.includes(dKey) || deptFloors.includes(flKey)) {
          return true;
        }
        if (deptFloors.length === 0) {
          return true;
        }
        return false;
      });
    }
    return formMeta.departments;
  }, [formMeta.departments, formMeta.floorDepartments, formMeta.departmentFloors, employeeForm.floor]);

  // 2. Floor locking & available floors based on selected Department (Add Employee)
  const addFloorConfig = useMemo(() => {
    if (!employeeForm.department) {
      return {
        isLocked: false,
        lockReason: null,
        availableFloors: formMeta.floors,
      };
    }

    const dKey = (employeeForm.department || "").toLowerCase();
    const deptFloors = formMeta.departmentFloors?.[dKey] || [];
    let targetFloors = deptFloors;
    if (!targetFloors || targetFloors.length === 0) {
      const match = formMeta.departments.find((d) => (d.name || "").toLowerCase() === dKey);
      if (match?.floors?.length) {
        targetFloors = match.floors;
      }
    }

    if (targetFloors && targetFloors.length === 1) {
      const singleFloorVal = String(targetFloors[0]);
      const matchedFloorObj = formMeta.floors.find((f) => String(f.value) === singleFloorVal);
      return {
        isLocked: true,
        lockReason: `Locked to ${matchedFloorObj?.label || `Floor ${singleFloorVal}`} for ${employeeForm.department}`,
        forcedFloor: singleFloorVal,
        availableFloors: formMeta.floors.filter((f) => String(f.value) === singleFloorVal),
      };
    }

    if (targetFloors && targetFloors.length > 1) {
      const allowedFloors = formMeta.floors.filter((f) => targetFloors.includes(String(f.value)));
      return {
        isLocked: false,
        lockReason: `Available on ${targetFloors.length} floors`,
        forcedFloor: null,
        availableFloors: allowedFloors.length > 0 ? allowedFloors : formMeta.floors,
      };
    }

    return {
      isLocked: false,
      lockReason: null,
      availableFloors: formMeta.floors,
    };
  }, [employeeForm.department, formMeta.departments, formMeta.floors, formMeta.departmentFloors]);

  // 3. Department change handler (Add Employee)
  const handleAddDeptChange = (val) => {
    const deptVal = typeof val === "object" ? val?.value : val;
    if (!deptVal) {
      autoGenerateCodes({ department: "", selected_existing_seat: "" });
      return;
    }

    const dKey = deptVal.toLowerCase();
    const deptFloors = formMeta.departmentFloors?.[dKey] || [];
    const deptObj = formMeta.departments.find((d) => (d.name || "").toLowerCase() === dKey);
    const floorsArr = (deptFloors.length > 0 ? deptFloors : deptObj?.floors) || [];

    let targetFloor = employeeForm.floor;
    if (floorsArr.length === 1) {
      targetFloor = String(floorsArr[0]);
    } else if (floorsArr.length > 1) {
      if (!floorsArr.includes(String(employeeForm.floor))) {
        targetFloor = String(floorsArr[0]);
      }
    }

    autoGenerateCodes({
      department: deptVal,
      floor: targetFloor,
      selected_existing_seat: "",
    });
  };

  // 4. Floor change handler (Add Employee)
  const handleAddFloorChange = (val) => {
    const floorVal = typeof val === "object" ? val?.value : val;
    const floorStr = String(floorVal ?? "");

    let currentDept = employeeForm.department;
    if (currentDept) {
      const dKey = currentDept.toLowerCase();
      const deptFloors = formMeta.departmentFloors?.[dKey] || [];
      if (deptFloors.length > 0 && !deptFloors.includes(floorStr)) {
        currentDept = "";
      }
    }

    autoGenerateCodes({
      floor: floorStr,
      department: currentDept,
      selected_existing_seat: "",
    });
  };

  // 5. Existing Seats filtered by department & floor (Add Employee)
  const existingSeatOptions = useMemo(() => {
    let list = formMeta.desks || [];

    if (employeeForm.department) {
      const deptKey = employeeForm.department.toLowerCase();
      const filteredByDept = list.filter((d) => (d.department || "").toLowerCase() === deptKey);
      if (filteredByDept.length > 0) {
        list = filteredByDept;
      }
    }

    if (employeeForm.floor !== "" && employeeForm.floor !== null && employeeForm.floor !== undefined) {
      const flStr = String(employeeForm.floor);
      const filteredByFloor = list.filter((d) => String(d.floor) === flStr);
      if (filteredByFloor.length > 0) {
        list = filteredByFloor;
      }
    }

    return list.map((d) => {
      const isOcc = Boolean(d.is_occupied);
      const isCab = Boolean(d.is_cabin);
      const typeLabel = isCab ? "Cabin" : "Desk";
      const icon = isOcc ? "👤" : "🟢";
      const statusText = isOcc
        ? `Occupied by ${d.occupant_name || "Colleague"}`
        : "Available";
      return {
        value: d.flat_number,
        label: `${icon} ${d.flat_number} (${typeLabel}) — ${statusText} [Fl ${d.floor}]`,
        desk: d,
      };
    });
  }, [formMeta.desks, employeeForm.department, employeeForm.floor]);

  // 6. Handle select existing seat (Add Employee)
  const handleSelectExistingSeat = (val) => {
    const seatCode = typeof val === "object" ? val?.value : val;
    const matchedDesk = (formMeta.desks || []).find((d) => d.flat_number === seatCode);

    const isCabin = matchedDesk?.is_cabin || seatCode?.includes("-c") || seatCode?.includes("CAB");

    setEmployeeForm((prev) => ({
      ...prev,
      selected_existing_seat: seatCode,
      desk_code: isCabin ? "" : seatCode,
      cabin_code: isCabin ? seatCode : "",
      department: matchedDesk?.department && matchedDesk.department !== "Desk" && matchedDesk.department !== "General"
        ? matchedDesk.department
        : prev.department,
      floor: matchedDesk?.floor !== undefined ? String(matchedDesk.floor) : prev.floor,
    }));
  };

  // 1b. Linked Departments based on selected Floor (Edit Employee)
  const editDepartmentOptions = useMemo(() => {
    if (!editEmployee) return formMeta.departments;
    if (editEmployee.floor !== "" && editEmployee.floor !== null && editEmployee.floor !== undefined) {
      const flKey = String(editEmployee.floor);
      const deptsOnThisFloor = (formMeta.floorDepartments?.[flKey] || []).map((d) => d.toLowerCase());
      return formMeta.departments.filter((dept) => {
        const dKey = (dept.name || "").toLowerCase();
        const deptFloors = formMeta.departmentFloors?.[dKey] || dept.floors || [];
        if (deptsOnThisFloor.includes(dKey) || deptFloors.includes(flKey)) {
          return true;
        }
        if (deptFloors.length === 0) {
          return true;
        }
        return false;
      });
    }
    return formMeta.departments;
  }, [formMeta.departments, formMeta.floorDepartments, formMeta.departmentFloors, editEmployee?.floor]);

  // 2b. Floor locking & available floors based on selected Department (Edit Employee)
  const editFloorConfig = useMemo(() => {
    if (!editEmployee || !editEmployee.department) {
      return {
        isLocked: false,
        lockReason: null,
        availableFloors: formMeta.floors,
      };
    }

    const dKey = (editEmployee.department || "").toLowerCase();
    const deptFloors = formMeta.departmentFloors?.[dKey] || [];
    let targetFloors = deptFloors;
    if (!targetFloors || targetFloors.length === 0) {
      const match = formMeta.departments.find((d) => (d.name || "").toLowerCase() === dKey);
      if (match?.floors?.length) {
        targetFloors = match.floors;
      }
    }

    if (targetFloors && targetFloors.length === 1) {
      const singleFloorVal = String(targetFloors[0]);
      const matchedFloorObj = formMeta.floors.find((f) => String(f.value) === singleFloorVal);
      return {
        isLocked: true,
        lockReason: `Locked to ${matchedFloorObj?.label || `Floor ${singleFloorVal}`} for ${editEmployee.department}`,
        forcedFloor: singleFloorVal,
        availableFloors: formMeta.floors.filter((f) => String(f.value) === singleFloorVal),
      };
    }

    if (targetFloors && targetFloors.length > 1) {
      const allowedFloors = formMeta.floors.filter((f) => targetFloors.includes(String(f.value)));
      return {
        isLocked: false,
        lockReason: `Available on ${targetFloors.length} floors`,
        forcedFloor: null,
        availableFloors: allowedFloors.length > 0 ? allowedFloors : formMeta.floors,
      };
    }

    return {
      isLocked: false,
      lockReason: null,
      availableFloors: formMeta.floors,
    };
  }, [editEmployee?.department, formMeta.departments, formMeta.floors, formMeta.departmentFloors]);

  // 3b. Department change handler (Edit Employee)
  const handleEditDeptChange = (val) => {
    if (!editEmployee) return;
    const deptVal = typeof val === "object" ? val?.value : val;
    if (!deptVal) {
      autoGenerateEditCodes({ department: "", selected_existing_seat: "" });
      return;
    }

    const dKey = deptVal.toLowerCase();
    const deptFloors = formMeta.departmentFloors?.[dKey] || [];
    const deptObj = formMeta.departments.find((d) => (d.name || "").toLowerCase() === dKey);
    const floorsArr = (deptFloors.length > 0 ? deptFloors : deptObj?.floors) || [];

    let targetFloor = editEmployee.floor;
    if (floorsArr.length === 1) {
      targetFloor = String(floorsArr[0]);
    } else if (floorsArr.length > 1) {
      if (!floorsArr.includes(String(editEmployee.floor))) {
        targetFloor = String(floorsArr[0]);
      }
    }

    autoGenerateEditCodes({
      department: deptVal,
      floor: targetFloor,
      selected_existing_seat: "",
    });
  };

  // 4b. Floor change handler (Edit Employee)
  const handleEditFloorChange = (val) => {
    if (!editEmployee) return;
    const floorVal = typeof val === "object" ? val?.value : val;
    const floorStr = String(floorVal ?? "");

    let currentDept = editEmployee.department;
    if (currentDept) {
      const dKey = currentDept.toLowerCase();
      const deptFloors = formMeta.departmentFloors?.[dKey] || [];
      if (deptFloors.length > 0 && !deptFloors.includes(floorStr)) {
        currentDept = "";
      }
    }

    autoGenerateEditCodes({
      floor: floorStr,
      department: currentDept,
      selected_existing_seat: "",
    });
  };

  // 5b. Existing Seats filtered by department & floor (Edit Employee)
  const editExistingSeatOptions = useMemo(() => {
    if (!editEmployee) return [];
    let list = formMeta.desks || [];

    if (editEmployee.department) {
      const deptKey = editEmployee.department.toLowerCase();
      const filteredByDept = list.filter((d) => (d.department || "").toLowerCase() === deptKey);
      if (filteredByDept.length > 0) {
        list = filteredByDept;
      }
    }

    if (editEmployee.floor !== "" && editEmployee.floor !== null && editEmployee.floor !== undefined) {
      const flStr = String(editEmployee.floor);
      const filteredByFloor = list.filter((d) => String(d.floor) === flStr);
      if (filteredByFloor.length > 0) {
        list = filteredByFloor;
      }
    }

    return list.map((d) => {
      const isOcc = Boolean(d.is_occupied);
      const isCab = Boolean(d.is_cabin);
      const typeLabel = isCab ? "Cabin" : "Desk";
      const icon = isOcc ? "👤" : "🟢";
      const isCurrent = editEmployee.desk_code === d.flat_number || editEmployee.cabin_code === d.flat_number;
      const statusText = isCurrent
        ? "Current Seat"
        : isOcc
          ? `Occupied by ${d.occupant_name || "Colleague"}`
          : "Available";
      return {
        value: d.flat_number,
        label: `${icon} ${d.flat_number} (${typeLabel}) — ${statusText} [Fl ${d.floor}]`,
        desk: d,
      };
    });
  }, [formMeta.desks, editEmployee?.department, editEmployee?.floor, editEmployee?.desk_code, editEmployee?.cabin_code]);

  // 6b. Handle select existing seat (Edit Employee)
  const handleEditSelectExistingSeat = (val) => {
    if (!editEmployee) return;
    const seatCode = typeof val === "object" ? val?.value : val;
    const matchedDesk = (formMeta.desks || []).find((d) => d.flat_number === seatCode);

    const isCabin = matchedDesk?.is_cabin || seatCode?.includes("-c") || seatCode?.includes("CAB");

    setEditEmployee((prev) => ({
      ...prev,
      selected_existing_seat: seatCode,
      desk_code: isCabin ? "" : seatCode,
      cabin_code: isCabin ? seatCode : "",
      department: matchedDesk?.department && matchedDesk.department !== "Desk" && matchedDesk.department !== "General"
        ? matchedDesk.department
        : prev.department,
      floor: matchedDesk?.floor !== undefined ? String(matchedDesk.floor) : prev.floor,
    }));
  };

  // Handlers for Creating / Assigning
  const handleExportEmployees = async () => {
    setExportLoading(true);
    try {
      await exportSocietyResidents(societyId, formMeta.propertyName || (isIndustrial ? "Corporate" : "Society"));
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportEmployees = async (e) => {
    e.preventDefault();
    if (!importFile) return;
    setImportLoading(true);
    try {
      const res = await importSocietyResidents(societyId, importFile);
      if (res?.success) {
        setIsImportEmployeeOpen(false);
        setImportFile(null);
        fetchTabData("residents");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setImportLoading(false);
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    if (!employeeForm.name) return;
    setSaving(true);
    try {
      const created = await createSocietyResident(societyId, employeeForm);
      if (created) {
        setIsAddEmployeeOpen(false);
        setEmployeeForm({
          name: "",
          phone: "",
          email: "",
          department: "",
          floor: "",
          designation: "",
          is_custom_designation: false,
          reports_to_user_id: "",
          seating_type: "DESK",
          seating_assignment_mode: "AUTO",
          selected_existing_seat: "",
          desk_code: "",
          cabin_code: "",
          extension: "",
          is_delegator: true,
          is_delegatee: true,
        });
        fetchTabData("residents");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAssignHOD = async (e) => {
    e.preventDefault();
    if (!assignHODData.residentId) return;
    setSaving(true);
    try {
      const res = await assignCommitteeRole(societyId, assignHODData);
      if (res || res === undefined) {
        setIsAssignHODOpen(false);
        setAssignHODData({
          residentId: "",
          role: leadershipConfig.roles[0]?.value || "Department Head",
          deskCode: "",
          cabinCode: "",
        });
        fetchTabData("management");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHODEdit = async (e) => {
    e.preventDefault();
    if (!memberToEdit) return;
    setSaving(true);
    try {
      await assignCommitteeRole(societyId, {
        residentId: memberToEdit.id || memberToEdit.user_id,
        role: editHODData.role,
        deskCode: editHODData.deskCode,
        cabinCode: editHODData.cabinCode,
      });
      setMemberToEdit(null);
      fetchTabData("management");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmployeeEdit = async (e) => {
    e.preventDefault();
    if (!editEmployee) return;
    setSaving(true);
    try {
      const updated = await updateSocietyResident(societyId, editEmployee.id || editEmployee.user_id, editEmployee);
      if (updated) {
        setEditEmployee(null);
        fetchTabData(activeTab);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmRevoke = async () => {
    if (!revokeTarget) return;

    // For resident / employee, check if desk code verification matches
    if (revokeTarget.type !== "management" && revokeTarget.expectedDeskCode) {
      if (!revokeDeskCodeInput.trim()) {
        setRevokeError(`Please enter the desk/cabin code (${revokeTarget.expectedDeskCode}) to confirm revocation.`);
        return;
      }
      if (revokeDeskCodeInput.trim().toUpperCase() !== revokeTarget.expectedDeskCode.trim().toUpperCase()) {
        setRevokeError(`Incorrect code. Please enter "${revokeTarget.expectedDeskCode}" exactly.`);
        return;
      }
    }

    setSaving(true);
    setRevokeError("");
    try {
      if (revokeTarget.type === "management") {
        await revokeCommitteeRole(societyId, revokeTarget.id);
        fetchTabData("management");
      } else {
        const res = await revokeSocietyResident(societyId, revokeTarget.id, {
          desk_code: revokeDeskCodeInput.trim(),
        });
        if (res) {
          fetchTabData(activeTab);
        }
      }
      setRevokeTarget(null);
      setRevokeDeskCodeInput("");
      setRevokeError("");
    } catch (err) {
      setRevokeError(err?.message || "Failed to revoke member.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateVehicle = async (e) => {
    e.preventDefault();
    if (!vehicleForm.registration_number) return;
    setSaving(true);
    try {
      const created = await createSocietyVehicle(societyId, vehicleForm);
      if (created) {
        setIsAddVehicleOpen(false);
        setVehicleForm({
          registration_number: "",
          vehicle_type: "Car",
          model: "",
          slot_code: "",
        });
        fetchTabData("vehicles");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCreateContractor = async (e) => {
    e.preventDefault();
    if (!contractorForm.vendor_name && !contractorForm.name) return;
    setSaving(true);
    try {
      const created = await createSocietyTenant(societyId, contractorForm);
      if (created) {
        setIsAddContractorOpen(false);
        setContractorForm({
          name: "",
          vendor_name: "",
          contact_number: "",
          area: "",
          contract_end: "",
        });
        fetchTabData(activeTab === "contractors" ? "contractors" : "tenants");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHotline = async (e) => {
    e.preventDefault();
    if (!hotlineForm.title || !hotlineForm.contact_number) return;
    setSaving(true);
    try {
      const res = await saveSocietyHotline(societyId, hotlineForm);
      if (res) {
        setIsHotlineModalOpen(false);
        setHotlineForm({
          id: "",
          title: "",
          subtitle: "",
          contact_number: "",
          extension: "",
          category: "GENERAL",
          is_mandatory: false,
        });
        fetchTabData("hotlines");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteHotline = async () => {
    if (!hotlineDeleteTarget) return;
    setSaving(true);
    try {
      const res = await deleteSocietyHotline(societyId, hotlineDeleteTarget.id || hotlineDeleteTarget.key);
      if (res) {
        setHotlineDeleteTarget(null);
        fetchTabData("hotlines");
      }
    } finally {
      setSaving(false);
    }
  };

  const tabs = isIndustrial
    ? [
        { id: "residents", label: "Employees & Staff" },
        { id: "management", label: "Leadership & HODs" },
        { id: "hotlines", label: "Campus Hotlines" },
        { id: "vehicles", label: "Company Fleet & Vehicles" },
        { id: "contractors", label: "Contractors & Logistics" },
      ]
    : isCommercial
    ? [
        { id: "tenants", label: "Store & Office Tenants" },
        { id: "residents", label: "Store Employees" },
        { id: "hotlines", label: "Premises Hotlines" },
        { id: "vehicles", label: "Customer & Delivery Vehicles" },
        { id: "contractors", label: "Vendors & Suppliers" },
      ]
    : [
        { id: "residents", label: "Residents" },
        { id: "tenants", label: "Tenants" },
        { id: "vehicles", label: "Vehicles" },
        { id: "pets", label: "Pets" },
      ];

  const getFilteredData = () => {
    const lowerQuery = searchQuery.toLowerCase();
    const currentData = data[activeTab] || [];

    if (!searchQuery) return currentData;

    return currentData.filter((item) => {
      const name = (item.title || item.name || item.full_name || item.vendor_name || "").toLowerCase();
      const subtitle = (item.subtitle || "").toLowerCase();
      const unit = (item.unit || item.flat_number || item.area || item.location || "").toLowerCase();
      const contact = (item.contact_number || item.phone || item.mobile_number || item.email || item.email_id || "").toLowerCase();
      const ext = (item.extension || item.ext || "").toLowerCase();
      const plate = (item.plate || item.vehicle_number || "").toLowerCase();
      const role = (item.role || item.designation || item.category || "").toLowerCase();
      const dept = (item.department || "").toLowerCase();

      return (
        name.includes(lowerQuery) ||
        subtitle.includes(lowerQuery) ||
        unit.includes(lowerQuery) ||
        contact.includes(lowerQuery) ||
        ext.includes(lowerQuery) ||
        plate.includes(lowerQuery) ||
        role.includes(lowerQuery) ||
        dept.includes(lowerQuery)
      );
    });
  };

  const filteredData = getFilteredData();
  const paginatedData = filteredData.slice((page - 1) * limit, page * limit);
  const currentTabLabel = tabs.find((t) => t.id === activeTab)?.label || "Directory";

  return (
    <div
      style={{
        padding: "24px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      {/* Tab Navigation and Search/Action Bar */}
      <div
        style={{
          display: "flex",
          gap: "24px",
          borderBottom: "1px solid #e2e8f0",
          marginBottom: "24px",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSearchQuery("");
                setPage(1);
              }}
              style={{
                background: "none",
                border: "none",
                padding: "10px 4px",
                fontSize: "15px",
                fontWeight: "600",
                cursor: "pointer",
                color: activeTab === tab.id ? "var(--color-dark-bg, #27306f)" : "#64748b",
                borderBottom:
                  activeTab === tab.id
                    ? "2px solid var(--color-dark-bg, #27306f)"
                    : "2px solid transparent",
                transition: "all 150ms ease",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            marginBottom: "8px",
            flexWrap: "wrap",
          }}
        >
          {/* Search Box */}
          <div style={{ position: "relative" }}>
            <Search
              size={16}
              color="#94a3b8"
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            />
            <input
              type="text"
              placeholder={`Search ${currentTabLabel.toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
              }}
              style={{
                padding: "8px 12px 8px 36px",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                fontSize: "13px",
                width: "220px",
                outline: "none",
                fontFamily: "inherit",
              }}
            />
          </div>

          {/* Import / Export & Add Employee Buttons */}
          {activeTab === "residents" && canManage && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={handleExportEmployees}
                disabled={exportLoading}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 13px",
                  background: "#ffffff",
                  color: "#1e293b",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: exportLoading ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                  transition: "all 0.15s ease",
                }}
              >
                <Download size={14} color="#64748b" />
                {exportLoading ? "Exporting..." : isIndustrial ? "Export Employees" : "Export Members"}
              </button>

              <button
                type="button"
                onClick={() => {
                  setImportFile(null);
                  setIsImportEmployeeOpen(true);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 13px",
                  background: "#ffffff",
                  color: "#1e293b",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                  transition: "all 0.15s ease",
                }}
              >
                <Upload size={14} color="#64748b" />
                {isIndustrial ? "Import Employees" : "Import Members"}
              </button>

              <button
                type="button"
                onClick={() => setIsAddEmployeeOpen(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 14px",
                  background: "var(--color-dark-bg, #27306f)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  boxShadow: "0 2px 4px rgba(39, 48, 111, 0.2)",
                }}
              >
                <Plus size={15} />
                {isIndustrial ? "Add Employee" : isCommercial ? "Add Store Staff" : "Add Resident"}
              </button>
            </div>
          )}

          {/* Add/Assign HOD Button */}
          {activeTab === "management" && canManage && (
            <button
              onClick={() => {
                if (data.residents.length === 0) fetchTabData("residents");
                setIsAssignHODOpen(true);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 14px",
                background: "var(--color-dark-bg, #27306f)",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                whiteSpace: "nowrap",
                boxShadow: "0 2px 4px rgba(39, 48, 111, 0.2)",
              }}
            >
              <Plus size={15} />
              {isIndustrial ? "Assign HOD / Leader" : "Assign Leader"}
            </button>
          )}

          {/* Add Custom Hotline Button */}
          {activeTab === "hotlines" && canManage && (
            <button
              onClick={() => {
                setHotlineForm({
                  id: "",
                  title: "",
                  subtitle: "",
                  contact_number: "",
                  extension: "",
                  category: "GENERAL",
                  is_mandatory: false,
                });
                setHotlineModalMode("add");
                setIsHotlineModalOpen(true);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 14px",
                background: "var(--color-dark-bg, #27306f)",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                whiteSpace: "nowrap",
                boxShadow: "0 2px 4px rgba(39, 48, 111, 0.2)",
              }}
            >
              <Plus size={15} />
              Add Custom Hotline
            </button>
          )}

          {/* Add Fleet Vehicle */}
          {activeTab === "vehicles" && (
            <button
              onClick={() => setIsAddVehicleOpen(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 14px",
                background: "var(--color-dark-bg, #27306f)",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              <Plus size={15} />
              {isIndustrial ? "Add Fleet Vehicle" : "Add Vehicle"}
            </button>
          )}

          {/* Add Contractor / Tenant */}
          {(activeTab === "contractors" || activeTab === "tenants") && (
            <button
              onClick={() => setIsAddContractorOpen(true)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 14px",
                background: "var(--color-dark-bg, #27306f)",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              <Plus size={15} />
              {isIndustrial
                ? "Register Contractor"
                : isCommercial
                ? "Add Store Tenant"
                : "Add Tenant"}
            </button>
          )}
        </div>
      </div>

      {/* Grid Cards Content (Consistent Card UI Across All Tabs) */}
      <div>
        {loading ? (
          <div style={{ padding: "60px", textAlign: "center", color: "#64748b" }}>
            Loading {currentTabLabel}...
          </div>
        ) : filteredData.length > 0 ? (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))",
                gap: "20px",
              }}
            >
              {paginatedData.map((item) => {
                const itemId = item.id || item.user_id || item.vehicle_id || Math.random();
                const displayName = item.name || item.full_name || item.vendor_name || item.plate || "Unknown";
                const initial = displayName.charAt(0).toUpperCase();

                return (
                  <div
                    key={itemId}
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "12px",
                      padding: "20px",
                      background: "#fff",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      transition: "transform 150ms ease, box-shadow 150ms ease",
                    }}
                  >
                    <div>
                      {/* Card Top: Avatar / Badge */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "14px",
                          marginBottom: "16px",
                        }}
                      >
                        <div
                          style={{
                            width: "50px",
                            height: "50px",
                            borderRadius: "50%",
                            background:
                              activeTab === "hotlines"
                                ? item.category === "EMERGENCY"
                                  ? "#fef2f2"
                                  : item.category === "LOGISTICS"
                                  ? "#fef3c7"
                                  : "#eff6ff"
                                : activeTab === "management"
                                ? "#e0e7ff"
                                : activeTab === "vehicles"
                                ? "#fef3c7"
                                : activeTab === "contractors"
                                ? "#ecfdf5"
                                : "#eff6ff",
                            color:
                              activeTab === "hotlines"
                                ? item.category === "EMERGENCY"
                                  ? "#dc2626"
                                  : item.category === "LOGISTICS"
                                  ? "#b45309"
                                  : "#2563eb"
                                : activeTab === "management"
                                ? "#4338ca"
                                : activeTab === "vehicles"
                                ? "#b45309"
                                : activeTab === "contractors"
                                ? "#047857"
                                : "#2563eb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "18px",
                            fontWeight: "700",
                            flexShrink: 0,
                          }}
                        >
                          {activeTab === "hotlines" ? (
                            <PhoneCall size={22} />
                          ) : activeTab === "vehicles" ? (
                            <Truck size={22} />
                          ) : (
                            initial
                          )}
                        </div>

                        <div style={{ minWidth: 0, flex: 1 }}>
                          <h4
                            style={{
                              margin: "0 0 4px 0",
                              color: "#0f172a",
                              fontSize: "15.5px",
                              fontWeight: "700",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {activeTab === "hotlines" ? item.title : displayName}
                          </h4>

                          {/* Role / Type Badge */}
                          <div
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "5px",
                              background:
                                activeTab === "hotlines"
                                  ? item.is_mandatory
                                    ? "#eff6ff"
                                    : "#f8fafc"
                                  : activeTab === "management"
                                  ? "#fef2f2"
                                  : "#f8fafc",
                              color:
                                activeTab === "hotlines"
                                  ? item.is_mandatory
                                    ? "#1d4ed8"
                                    : "#475569"
                                  : activeTab === "management"
                                  ? "#dc2626"
                                  : "var(--color-dark-bg, #27306f)",
                              border: "1px solid #e2e8f0",
                              padding: "2px 8px",
                              borderRadius: "10px",
                              fontSize: "11.5px",
                              fontWeight: "600",
                            }}
                          >
                            {activeTab === "hotlines" && (item.is_mandatory ? <Lock size={11} /> : <PhoneCall size={11} />)}
                            {activeTab === "management" && <Award size={12} />}
                            {activeTab === "residents" && <User size={12} />}
                            {activeTab === "vehicles" && <Truck size={12} />}
                            {activeTab === "contractors" && <Shield size={12} />}
                            {activeTab === "hotlines"
                              ? (item.is_mandatory ? "Mandatory Campus Hotline" : "Custom Hotline")
                              : (item.role || item.designation || item.type || (activeTab === "contractors" ? "Contractor" : "Member"))}
                          </div>
                        </div>
                      </div>

                      {/* Card Details Body */}
                      <div
                        style={{
                          borderTop: "1px solid #f1f5f9",
                          paddingTop: "14px",
                          display: "flex",
                          flexDirection: "column",
                          gap: "9px",
                        }}
                      >
                        {/* 1. Hotline Card Details */}
                        {activeTab === "hotlines" ? (
                          <>
                            {item.subtitle && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#334155",
                                  fontSize: "13px",
                                  fontWeight: "500",
                                }}
                              >
                                <Briefcase size={14} color="#94a3b8" />
                                <span>{item.subtitle}</span>
                              </div>
                            )}

                            {item.extension && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#475569",
                                  fontSize: "13px",
                                }}
                              >
                                <Phone size={14} color="#94a3b8" /> Intercom Extension:
                                <span
                                  style={{
                                    background: "#f1f5f9",
                                    color: "var(--color-dark-bg, #27306f)",
                                    border: "1px solid #cbd5e1",
                                    padding: "2px 7px",
                                    borderRadius: "6px",
                                    fontWeight: 700,
                                    fontSize: "12px",
                                  }}
                                >
                                  Ext {item.extension}
                                </span>
                              </div>
                            )}

                            {item.contact_number && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#475569",
                                  fontSize: "13px",
                                }}
                              >
                                <PhoneCall size={14} color="#94a3b8" /> Contact Number:
                                <strong style={{ color: "#0f172a" }}>
                                  {item.contact_number}
                                </strong>
                              </div>
                            )}

                            {item.category && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#475569",
                                  fontSize: "12.5px",
                                }}
                              >
                                <Hash size={14} color="#94a3b8" /> Category:
                                <span
                                  style={{
                                    background: "#f8fafc",
                                    color: "#475569",
                                    border: "1px solid #e2e8f0",
                                    padding: "1px 6px",
                                    borderRadius: "4px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    textTransform: "uppercase",
                                  }}
                                >
                                  {item.category}
                                </span>
                              </div>
                            )}
                          </>
                        ) : activeTab === "vehicles" ? (
                          <>
                            {/* 2. Vehicles Card Details */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                color: "#475569",
                                fontSize: "13px",
                              }}
                            >
                              <Hash size={14} color="#94a3b8" /> Plate Number:
                              <span
                                style={{
                                  fontFamily: "monospace",
                                  fontWeight: 700,
                                  color: "var(--color-dark-bg)",
                                }}
                              >
                                {item.plate || item.vehicle_number || "N/A"}
                              </span>
                            </div>

                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                color: "#475569",
                                fontSize: "13px",
                              }}
                            >
                              <User size={14} color="#94a3b8" /> Driver / Assigned:
                              <strong>{item.owner || item.driver_name || "Company Fleet"}</strong>
                            </div>

                            {(item.phone || item.mobile_number || item.contact_number) && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#475569",
                                  fontSize: "13px",
                                }}
                              >
                                <Phone size={14} color="#94a3b8" />
                                <span>{item.phone || item.mobile_number || item.contact_number}</span>
                              </div>
                            )}

                            {(item.unit || item.flat_number) && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#475569",
                                  fontSize: "13px",
                                }}
                              >
                                <Home size={14} color="#94a3b8" />
                                <span>Unit: {item.unit || item.flat_number}</span>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            {/* 3. Employee, Management & Resident Details */}
                            {/* Intercom Extension */}
                            {(item.extension || item.ext) && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#475569",
                                  fontSize: "13px",
                                }}
                              >
                                <Phone size={14} color="#94a3b8" /> Intercom Extension:
                                <span
                                  style={{
                                    background: "#f1f5f9",
                                    color: "var(--color-dark-bg, #27306f)",
                                    border: "1px solid #cbd5e1",
                                    padding: "2px 7px",
                                    borderRadius: "6px",
                                    fontWeight: 700,
                                    fontSize: "12px",
                                  }}
                                >
                                  Ext {item.extension || item.ext}
                                </span>
                              </div>
                            )}

                            {/* Cabin or Desk / Workstation / Location (Without Duplication) */}
                            {item.cabin_code ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#475569",
                                  fontSize: "13px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <DoorOpen size={14} color="#0d9488" />
                                <span>Cabin:</span>
                                <span
                                  style={{
                                    background: "#f0fdfa",
                                    color: "#0d9488",
                                    border: "1px solid #ccfbf1",
                                    padding: "1px 6px",
                                    borderRadius: "4px",
                                    fontWeight: 700,
                                    fontSize: "11.5px",
                                  }}
                                >
                                  Cabin {item.cabin_code}
                                </span>
                                {item.desk_code && item.desk_code !== item.cabin_code && (
                                  <span
                                    style={{
                                      background: "#eff6ff",
                                      color: "var(--color-dark-bg)",
                                      border: "1px solid #dbeafe",
                                      padding: "1px 6px",
                                      borderRadius: "4px",
                                      fontWeight: 700,
                                      fontSize: "11px",
                                    }}
                                  >
                                    Desk: {item.desk_code}
                                  </span>
                                )}
                              </div>
                            ) : item.desk_code ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#475569",
                                  fontSize: "13px",
                                }}
                              >
                                <Laptop size={14} color="#94a3b8" /> Workstation / Desk:
                                <span
                                  style={{
                                    background: "#eff6ff",
                                    color: "var(--color-dark-bg)",
                                    border: "1px solid #dbeafe",
                                    padding: "1px 6px",
                                    borderRadius: "4px",
                                    fontWeight: 700,
                                    fontSize: "11.5px",
                                  }}
                                >
                                  {item.desk_code}
                                </span>
                                {item.floor && (
                                  <span style={{ fontSize: "12px", color: "#64748b" }}>
                                    (Floor {item.floor === "0" ? "Ground" : item.floor})
                                  </span>
                                )}
                              </div>
                            ) : (
                              (item.unit || item.flat_number || item.area || item.location) && (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                    color: "#475569",
                                    fontSize: "13px",
                                  }}
                                >
                                  <Home size={14} color="#94a3b8" />
                                  <span>
                                    {isIndustrial ? "Location / Unit" : isCommercial ? "Space" : "Flat / Unit"}:
                                  </span>
                                  <strong style={{ color: "#0f172a" }}>
                                    {item.unit || item.flat_number || item.area || item.location}
                                  </strong>
                                </div>
                              )
                            )}

                            {/* Department */}
                            {item.department && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#475569",
                                  fontSize: "13px",
                                }}
                              >
                                <Briefcase size={14} color="#94a3b8" /> Department:
                                <strong style={{ color: "#0f172a" }}>{item.department}</strong>
                              </div>
                            )}

                            {/* Phone */}
                            {(item.phone || item.mobile_number || item.contact_number) && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#475569",
                                  fontSize: "13px",
                                }}
                              >
                                <Phone size={14} color="#94a3b8" />
                                <span>{item.phone || item.mobile_number || item.contact_number}</span>
                              </div>
                            )}

                            {/* Email */}
                            {(item.email || item.email_id) && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#475569",
                                  fontSize: "13px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                <Mail size={14} color="#94a3b8" />
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {item.email || item.email_id}
                                </span>
                              </div>
                            )}

                            {/* Contractor / Tenant Lease details */}
                            {(activeTab === "contractors" || activeTab === "tenants") && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  color: "#475569",
                                  fontSize: "13px",
                                }}
                              >
                                <Clock size={14} color="#94a3b8" /> Agreement End:
                                <span>{item.leaseEnd || item.contract_end || "Ongoing"}</span>
                              </div>
                            )}
                          </>
                        )}</div>
                    </div>

                    {/* Action Buttons: View, Edit, Revoke / Delete (Square Icon Buttons aligned right) */}
                    <div
                      style={{
                        marginTop: "14px",
                        paddingTop: "10px",
                        borderTop: "1px solid #f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: "8px",
                      }}
                    >
                      {activeTab === "hotlines" ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setViewMember(item)}
                            title="View hotline details"
                            style={{
                              width: "32px",
                              height: "32px",
                              padding: 0,
                              background: "#f8fafc",
                              border: "1px solid #e2e8f0",
                              color: "#334155",
                              borderRadius: "6px",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.15s ease",
                            }}
                          >
                            <Eye size={15} />
                          </button>

                          {canManage && (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setHotlineForm({
                                    id: item.id || item.key || "",
                                    title: item.title || "",
                                    subtitle: item.subtitle || "",
                                    contact_number: item.contact_number || "",
                                    extension: item.extension || "",
                                    category: item.category || "GENERAL",
                                    is_mandatory: !!item.is_mandatory,
                                  });
                                  setHotlineModalMode("edit");
                                  setIsHotlineModalOpen(true);
                                }}
                                title="Edit hotline contact & extension"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  padding: 0,
                                  background: "#eff6ff",
                                  border: "1px solid #bfdbfe",
                                  color: "var(--color-dark-bg, #27306f)",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.15s ease",
                                }}
                              >
                                <Edit2 size={15} />
                              </button>

                              {item.is_mandatory ? (
                                <button
                                  type="button"
                                  disabled
                                  title="Mandatory campus hotline cannot be deleted"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    padding: 0,
                                    background: "#f1f5f9",
                                    border: "1px solid #e2e8f0",
                                    color: "#94a3b8",
                                    borderRadius: "6px",
                                    cursor: "not-allowed",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    opacity: 0.8,
                                  }}
                                >
                                  <Lock size={14} />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setHotlineDeleteTarget(item)}
                                  title="Delete custom hotline"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    padding: 0,
                                    background: "#fef2f2",
                                    border: "1px solid #fecaca",
                                    color: "#dc2626",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    transition: "all 0.15s ease",
                                  }}
                                >
                                  <Trash2 size={15} />
                                </button>
                              )}
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={() => setViewMember(item)}
                            title="View profile details"
                            style={{
                              width: "32px",
                              height: "32px",
                              padding: 0,
                              background: "#f8fafc",
                              border: "1px solid #e2e8f0",
                              color: "#334155",
                              borderRadius: "6px",
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.15s ease",
                            }}
                          >
                            <Eye size={15} />
                          </button>

                          {canManage && (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  if (activeTab === "management") {
                                    setMemberToEdit(item);
                                    setEditHODData({
                                      role: item.role || item.designation || leadershipConfig.roles[0]?.value || "Member",
                                      deskCode: item.desk_code || "",
                                      cabinCode: item.cabin_code || "",
                                    });
                                  } else {
                                    setEditEmployee({
                                      id: item.id || item.user_id,
                                      name: item.name || item.full_name || "",
                                      phone: item.phone || item.mobile_number || "",
                                      email: item.email || item.email_id || "",
                                      department: item.department || "",
                                      floor: item.floor !== undefined && item.floor !== null ? String(item.floor) : "",
                                      designation: item.designation || item.role || "",
                                      is_custom_designation: Boolean(item.designation || item.role) && !STANDARD_DESIGNATIONS.includes(item.designation || item.role),
                                      reports_to_user_id: item.reports_to_user_id || "",
                                      seating_type: item.seating_type || (item.cabin_code ? "CABIN" : "DESK"),
                                      seating_assignment_mode: item.desk_code || item.cabin_code ? "EXISTING" : "AUTO",
                                      selected_existing_seat: item.desk_code || item.cabin_code || "",
                                      desk_code: item.desk_code || "",
                                      cabin_code: item.cabin_code || "",
                                      extension: item.extension || item.ext || "",
                                      is_delegator: item.is_delegator !== undefined ? item.is_delegator : true,
                                      is_delegatee: item.is_delegatee !== undefined ? item.is_delegatee : true,
                                    });
                                  }
                                }}
                                title="Edit details"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  padding: 0,
                                  background: "#eff6ff",
                                  border: "1px solid #bfdbfe",
                                  color: "var(--color-dark-bg, #27306f)",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.15s ease",
                                }}
                              >
                                <Edit2 size={15} />
                              </button>

                              <button
                                type="button"
                                onClick={() => {
                                  const deskCode = (item.desk_code || item.cabin_code || "").trim();
                                  setRevokeTarget({
                                    id: item.id || item.user_id,
                                    name: item.name || item.full_name || "Member",
                                    role: item.role || item.designation || "Member",
                                    type: activeTab === "management" ? "management" : "resident",
                                    expectedDeskCode: deskCode,
                                  });
                                  setRevokeDeskCodeInput("");
                                  setRevokeError("");
                                }}
                                title="Revoke access"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  padding: 0,
                                  background: "#fef2f2",
                                  border: "1px solid #fecaca",
                                  color: "#dc2626",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.15s ease",
                                }}
                              >
                                <UserX size={15} />
                              </button>
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: "24px" }}>
              <TablePagination
                totalItems={filteredData.length}
                page={page}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={setLimit}
              />
            </div>
          </>
        ) : (
          <div style={{ padding: "60px", textAlign: "center", color: "#64748b" }}>
            {searchQuery
              ? `No ${currentTabLabel.toLowerCase()} match "${searchQuery}".`
              : `No ${currentTabLabel.toLowerCase()} registered in this property yet.`}
          </div>
        )}
      </div>

      {/* Modal: Import Employees Spreadsheet */}
      <Modal
        isOpen={isImportEmployeeOpen}
        onClose={() => setIsImportEmployeeOpen(false)}
        size="md"
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div style={{ background: "#eff6ff", color: "#2563eb", padding: "6px", borderRadius: "8px", display: "flex" }}>
              <FileSpreadsheet size={18} />
            </div>
            <span>{isIndustrial ? "Import Employees Spreadsheet" : "Import Members Spreadsheet"}</span>
          </div>
        }
      >
        <form onSubmit={handleImportEmployees} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <p style={{ margin: "0 0 10px 0", fontSize: "13px", color: "#475569", lineHeight: "1.5" }}>
              Upload an Excel (<code style={{ background: "#f1f5f9", padding: "2px 5px", borderRadius: "4px" }}>.xlsx</code>) or CSV file containing employee records. Existing members with matching phone or email will have their department, designation, and desk assignments updated.
            </p>
            <div style={{ background: "#f8fafc", border: "1px dashed #cbd5e1", borderRadius: "8px", padding: "16px", textAlign: "center" }}>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                id="employee-import-input"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                style={{ display: "none" }}
              />
              <label
                htmlFor="employee-import-input"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 16px",
                  background: "#ffffff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#334155",
                  cursor: "pointer",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                }}
              >
                <Upload size={15} color="#2563eb" />
                {importFile ? importFile.name : "Choose Excel or CSV File"}
              </label>
              {importFile && (
                <div style={{ marginTop: "8px", fontSize: "12px", color: "#16a34a", fontWeight: "600" }}>
                  Selected: {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "8px", borderTop: "1px solid #f1f5f9" }}>
            <button
              type="button"
              onClick={downloadEmployeeTemplate}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "transparent",
                border: "none",
                color: "#2563eb",
                fontSize: "12.5px",
                fontWeight: "600",
                cursor: "pointer",
                padding: 0,
              }}
            >
              <Download size={14} /> Download Sample Template
            </button>

            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                onClick={() => setIsImportEmployeeOpen(false)}
                style={{
                  padding: "8px 14px",
                  background: "#f1f5f9",
                  border: "none",
                  borderRadius: "6px",
                  color: "#475569",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={importLoading || !importFile}
                style={{
                  padding: "8px 16px",
                  background: importLoading || !importFile ? "#94a3b8" : "var(--color-dark-bg, #27306f)",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: importLoading || !importFile ? "not-allowed" : "pointer",
                }}
              >
                {importLoading ? "Importing..." : "Upload & Import"}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      {/* Modal: Add Employee Directly */}
      <Modal
        isOpen={isAddEmployeeOpen}
        onClose={() => setIsAddEmployeeOpen(false)}
        size="lg"
        title={isIndustrial ? "Add Employee / Colleague" : "Add Team Member"}
      >
        <form onSubmit={handleCreateEmployee} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div style={{ marginBottom: "-4px" }}>
            <FormField label="Full Name" required>
              <Input
                required
                placeholder="e.g. Rahul Sharma"
                value={employeeForm.name}
                onChange={(e) => setEmployeeForm({ ...employeeForm, name: e.target.value })}
                style={{ height: "38px", minHeight: "38px", padding: "8px 12px", borderRadius: "10px" }}
              />
            </FormField>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "-4px" }}>
            <FormField label="Phone Number">
              <Input
                type="tel"
                placeholder="+91 9876543210"
                value={employeeForm.phone}
                onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })}
                style={{ height: "38px", minHeight: "38px", padding: "8px 12px", borderRadius: "10px" }}
              />
            </FormField>
            <FormField label="Work Email">
              <Input
                type="email"
                placeholder="name@company.com"
                value={employeeForm.email}
                onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                style={{ height: "38px", minHeight: "38px", padding: "8px 12px", borderRadius: "10px" }}
              />
            </FormField>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "-4px" }}>
            <FormField label="Department" required>
              <Select
                value={employeeForm.department}
                onChange={handleAddDeptChange}
                placeholder="Select Department..."
                options={addDepartmentOptions.map((dept) => {
                  const dKey = dept.name.toLowerCase();
                  const dFloors = formMeta.departmentFloors?.[dKey] || dept.floors || [];
                  const floorSuffix = dFloors.length === 1
                    ? ` (${dFloors[0] === "0" ? "Ground" : `Floor ${dFloors[0]}`})`
                    : dFloors.length > 1
                      ? ` (${dFloors.map((fl) => fl === "0" ? "Ground" : `Fl ${fl}`).join(", ")})`
                      : "";
                  return {
                    value: dept.name,
                    label: `${dept.name}${floorSuffix}`,
                  };
                })}
              />
            </FormField>
            <FormField label="Designation / Role" required>
              <Select
                value={
                  employeeForm.is_custom_designation
                    ? "Other (Custom Designation)"
                    : employeeForm.designation
                }
                onChange={(val) => {
                  const selected = typeof val === "object" ? val?.value : val;
                  if (selected === "Other (Custom Designation)") {
                    setEmployeeForm((prev) => ({
                      ...prev,
                      designation: "",
                      is_custom_designation: true,
                    }));
                  } else {
                    setEmployeeForm((prev) => ({
                      ...prev,
                      designation: selected,
                      is_custom_designation: false,
                    }));
                  }
                }}
                placeholder="Select Designation / Role..."
                options={designationOptions}
              />
              {employeeForm.is_custom_designation && (
                <Input
                  required
                  autoFocus
                  placeholder="Type custom designation / role..."
                  value={employeeForm.designation}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, designation: e.target.value })}
                  style={{ marginTop: "6px", height: "36px", minHeight: "36px", padding: "6px 12px", borderRadius: "8px" }}
                />
              )}
            </FormField>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "-4px" }}>
            <FormField label="Floor">
              <Select
                disabled={addFloorConfig.isLocked}
                value={employeeForm.floor}
                onChange={handleAddFloorChange}
                placeholder="Select Floor..."
                options={addFloorConfig.availableFloors.map((f) => ({
                  value: String(f.value),
                  label: f.label,
                }))}
              />
              {addFloorConfig.isLocked && (
                <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "3px", display: "flex", alignItems: "center", gap: "4px" }}>
                  <span>🔒</span>
                  <span>{addFloorConfig.lockReason}</span>
                </div>
              )}
            </FormField>
            <FormField label="Reports To (HOD / Manager)">
              <Select
                value={employeeForm.reports_to_user_id}
                onChange={(val) => setEmployeeForm({ ...employeeForm, reports_to_user_id: typeof val === "object" ? val?.value : val })}
                placeholder="Directly to Leadership / None"
                isClearable
                options={formMeta.managers.map((mgr) => ({
                  value: String(mgr.value),
                  label: mgr.label,
                }))}
              />
            </FormField>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "-4px" }}>
            <FormField label="Seating Assignment Mode">
              <Select
                value={employeeForm.seating_assignment_mode || "AUTO"}
                onChange={(val) => {
                  const modeVal = typeof val === "object" ? val?.value : val;
                  if (modeVal === "EXISTING") {
                    setEmployeeForm({
                      ...employeeForm,
                      seating_assignment_mode: "EXISTING",
                    });
                  } else {
                    autoGenerateCodes({
                      seating_assignment_mode: "AUTO",
                      selected_existing_seat: "",
                    });
                  }
                }}
                options={[
                  { value: "AUTO", label: "Generate New Space" },
                  { value: "EXISTING", label: "Assign Pre-existing Seat / Cabin" },
                ]}
              />
            </FormField>

            {employeeForm.seating_assignment_mode === "EXISTING" ? (
              <FormField label="Select Existing Seat / Cabin" required>
                <Select
                  value={employeeForm.selected_existing_seat}
                  onChange={handleSelectExistingSeat}
                  placeholder="Choose existing desk, cabin, or cabin-desk..."
                  options={existingSeatOptions}
                />
              </FormField>
            ) : (
              <FormField label="Space / Seating Type">
                <Select
                  value={employeeForm.seating_type || "DESK"}
                  onChange={(val) => {
                    const typeVal = typeof val === "object" ? val?.value : val;
                    autoGenerateCodes({ seating_type: typeVal });
                  }}
                  options={[
                    { value: "DESK", label: "Dedicated Desk on Floor" },
                    { value: "CABIN", label: "Private Cabin (includes Cabin Desk)" },
                  ]}
                />
              </FormField>
            )}
          </div>

          {/* Code Inputs: Desk Code (Auto-generated), Cabin Code, Intercom Ext. (Editable) */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px", alignItems: "start", marginTop: "2px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <span style={{ fontSize: "12.5px", fontWeight: "600", color: "#475569", lineHeight: "1.2", minHeight: "26px", display: "flex", alignItems: "flex-end" }}>
                Desk Code (Auto-generated)
              </span>
              <Input
                disabled
                placeholder="Auto-generated"
                value={employeeForm.desk_code || "Auto-generated"}
                style={{ height: "38px", minHeight: "38px", padding: "8px 12px", borderRadius: "10px", background: "#f8fafc" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <span style={{ fontSize: "12.5px", fontWeight: "600", color: "#475569", lineHeight: "1.2", minHeight: "26px", display: "flex", alignItems: "flex-end" }}>
                Cabin Code {employeeForm.seating_type === "CABIN" || employeeForm.cabin_code ? "(Auto-generated)" : "(N/A)"}
              </span>
              <Input
                disabled
                placeholder={employeeForm.seating_type === "CABIN" || employeeForm.cabin_code ? "Auto-generated" : "N/A"}
                value={
                  employeeForm.seating_type === "CABIN" || employeeForm.cabin_code
                    ? employeeForm.cabin_code || "Auto-generated"
                    : "N/A"
                }
                style={{ height: "38px", minHeight: "38px", padding: "8px 12px", borderRadius: "10px", background: "#f8fafc" }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <span style={{ fontSize: "12.5px", fontWeight: "600", color: "#334155", lineHeight: "1.2", minHeight: "26px", display: "flex", alignItems: "flex-end" }}>
                Intercom Ext. (Editable)
              </span>
              <Input
                type="text"
                placeholder="e.g. 101"
                value={employeeForm.extension}
                onChange={(e) => setEmployeeForm({ ...employeeForm, extension: e.target.value })}
                style={{ height: "38px", minHeight: "38px", padding: "8px 12px", borderRadius: "10px" }}
              />
            </div>
          </div>
          {/* Delegation Authority Settings: Is Delegator & Is Delegatee */}
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "12px 14px", marginTop: "2px" }}>
            <span style={{ fontSize: "12.5px", fontWeight: "700", color: "#334155", display: "block", marginBottom: "8px" }}>
              Visitor Approval Delegation Capabilities
            </span>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer", userSelect: "none" }}>
                <input
                  type="checkbox"
                  checked={employeeForm.is_delegator !== false}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, is_delegator: e.target.checked })}
                  style={{ marginTop: "2px", accentColor: "var(--color-dark-bg, #27306f)", cursor: "pointer", width: "16px", height: "16px" }}
                />
                <span style={{ display: "flex", flexDirection: "column" }}>
                  <strong style={{ fontSize: "12.5px", color: "#1e293b" }}>Is Delegator</strong>
                  <span style={{ fontSize: "11px", color: "#64748b", lineHeight: 1.3 }}>
                    Can delegate own visitor approvals to eligible colleagues when away or busy.
                  </span>
                </span>
              </label>

              <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer", userSelect: "none" }}>
                <input
                  type="checkbox"
                  checked={employeeForm.is_delegatee !== false}
                  onChange={(e) => setEmployeeForm({ ...employeeForm, is_delegatee: e.target.checked })}
                  style={{ marginTop: "2px", accentColor: "var(--color-dark-bg, #27306f)", cursor: "pointer", width: "16px", height: "16px" }}
                />
                <span style={{ display: "flex", flexDirection: "column" }}>
                  <strong style={{ fontSize: "12.5px", color: "#1e293b" }}>Is Delegatee</strong>
                  <span style={{ fontSize: "11px", color: "#64748b", lineHeight: 1.3 }}>
                    Appears in colleagues&apos; delegate list to receive and act on delegated visitor entries.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "14px" }}>
            <button
              type="button"
              onClick={() => setIsAddEmployeeOpen(false)}
              style={{
                padding: "8px 16px",
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "8px 18px",
                background: "var(--color-dark-bg, #27306f)",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              {saving ? "Adding..." : "Add Employee"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal: Assign HOD / Leader */}
      <Modal
        isOpen={isAssignHODOpen}
        onClose={() => setIsAssignHODOpen(false)}
        title={leadershipConfig.modalTitle}
      >
        <form onSubmit={handleAssignHOD} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "5px" }}>
              {leadershipConfig.userSelectLabel}
            </label>
            <Select
              value={assignHODData.residentId}
              onChange={(e) => setAssignHODData({ ...assignHODData, residentId: e.target.value })}
              placeholder={leadershipConfig.userPlaceholder}
              options={data.residents.map((r) => ({
                value: r.id || r.user_id,
                label: `${r.name || r.full_name} (${r.department || r.unit || "General"})`,
              }))}
            />
          </div>

          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "5px" }}>
              {leadershipConfig.roleFieldLabel}
            </label>
            <Select
              value={assignHODData.role}
              onChange={(e) => setAssignHODData({ ...assignHODData, role: e.target.value })}
              options={leadershipConfig.roles.map((r) => ({
                value: r.value,
                label: r.label,
              }))}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "5px" }}>
                Cabin Code (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g. CAB-301"
                value={assignHODData.cabinCode}
                onChange={(e) => setAssignHODData({ ...assignHODData, cabinCode: e.target.value })}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "13.5px",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "5px" }}>
                Desk Code (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g. IT-01"
                value={assignHODData.deskCode}
                onChange={(e) => setAssignHODData({ ...assignHODData, deskCode: e.target.value })}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "13.5px",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "14px" }}>
            <button
              type="button"
              onClick={() => setIsAssignHODOpen(false)}
              style={{
                padding: "8px 16px",
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !assignHODData.residentId}
              style={{
                padding: "8px 18px",
                background: "var(--color-dark-bg, #27306f)",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: assignHODData.residentId ? "pointer" : "not-allowed",
                fontWeight: "600",
                opacity: assignHODData.residentId ? 1 : 0.6,
              }}
            >
              {saving ? "Assigning..." : "Assign Leadership Role"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal: Edit HOD Role */}
      {memberToEdit && (
        <Modal
          isOpen={true}
          onClose={() => setMemberToEdit(null)}
          title={`Edit Role for ${memberToEdit.name || "Leader"}`}
        >
          <form onSubmit={handleSaveHODEdit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "5px" }}>
                Role Title
              </label>
              <input
                type="text"
                required
                value={editHODData.role}
                onChange={(e) => setEditHODData({ ...editHODData, role: e.target.value })}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "13.5px",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "5px" }}>
                  Cabin Code
                </label>
                <input
                  type="text"
                  value={editHODData.cabinCode}
                  onChange={(e) => setEditHODData({ ...editHODData, cabinCode: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    fontSize: "13.5px",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "5px" }}>
                  Desk Code
                </label>
                <input
                  type="text"
                  value={editHODData.deskCode}
                  onChange={(e) => setEditHODData({ ...editHODData, deskCode: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    fontSize: "13.5px",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "14px" }}>
              <button
                type="button"
                onClick={() => setMemberToEdit(null)}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: "8px 18px",
                  background: "var(--color-dark-bg, #27306f)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal: Add Vehicle */}
      <Modal
        isOpen={isAddVehicleOpen}
        onClose={() => setIsAddVehicleOpen(false)}
        title={isIndustrial ? "Add Company Fleet Vehicle" : "Add Vehicle"}
      >
        <form onSubmit={handleCreateVehicle} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px" }}>
              Registration Number / Number Plate *
            </label>
            <input
              type="text"
              required
              placeholder="e.g. MH-04-AB-1234"
              value={vehicleForm.registration_number}
              onChange={(e) => setVehicleForm({ ...vehicleForm, registration_number: e.target.value.toUpperCase() })}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px" }}>
              Vehicle Type
            </label>
            <Select
              value={vehicleForm.vehicle_type}
              onChange={(e) => setVehicleForm({ ...vehicleForm, vehicle_type: e.target.value })}
              options={[
                { value: "Car", label: "Car / Four-Wheeler" },
                { value: "Bike", label: "Bike / Two-Wheeler" },
                { value: "Scooter", label: "Scooter" },
                { value: "Bicycle", label: "Bicycle" },
              ]}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px" }}>
                Make / Model
              </label>
              <input
                type="text"
                placeholder="e.g. Tata Nexon"
                value={vehicleForm.model}
                onChange={(e) => setVehicleForm({ ...vehicleForm, model: e.target.value })}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px" }}>
                Parking Slot Code (Optional)
              </label>
              <input
                type="text"
                placeholder="e.g. B1-04"
                value={vehicleForm.slot_code}
                onChange={(e) => setVehicleForm({ ...vehicleForm, slot_code: e.target.value })}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "12px" }}>
            <button
              type="button"
              onClick={() => setIsAddVehicleOpen(false)}
              style={{
                padding: "8px 16px",
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "8px 18px",
                background: "var(--color-dark-bg, #27306f)",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              {saving ? "Saving..." : "Save Vehicle"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal: Add Contractor / Tenant */}
      <Modal
        isOpen={isAddContractorOpen}
        onClose={() => setIsAddContractorOpen(false)}
        title={
          isIndustrial
            ? "Register Logistics / Contractor"
            : isCommercial
            ? "Add Store Tenant"
            : "Add Tenant"
        }
      >
        <form onSubmit={handleCreateContractor} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px" }}>
              {isIndustrial ? "Vendor / Company Name *" : "Tenant Name *"}
            </label>
            <input
              type="text"
              required
              placeholder={isIndustrial ? "e.g. BlueDart Logistics" : "e.g. Star Enterprises"}
              value={contractorForm.vendor_name}
              onChange={(e) => setContractorForm({ ...contractorForm, vendor_name: e.target.value })}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px" }}>
                Contact Person Name
              </label>
              <input
                type="text"
                placeholder="e.g. Amit Patel"
                value={contractorForm.name}
                onChange={(e) => setContractorForm({ ...contractorForm, name: e.target.value })}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px" }}>
                Phone Number
              </label>
              <input
                type="tel"
                placeholder="+91 9876543210"
                value={contractorForm.contact_number}
                onChange={(e) => setContractorForm({ ...contractorForm, contact_number: e.target.value })}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px" }}>
              {isIndustrial ? "Assigned Loading Bay / Work Area" : "Assigned Unit / Shop"}
            </label>
            <input
              type="text"
              placeholder="e.g. Bay 2, Ground Floor Electrical Room"
              value={contractorForm.area}
              onChange={(e) => setContractorForm({ ...contractorForm, area: e.target.value })}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px" }}>
              Contract / Agreement Expiry
            </label>
            <input
              type="date"
              value={contractorForm.contract_end}
              onChange={(e) => setContractorForm({ ...contractorForm, contract_end: e.target.value })}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #cbd5e1",
                borderRadius: "6px",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "12px" }}>
            <button
              type="button"
              onClick={() => setIsAddContractorOpen(false)}
              style={{
                padding: "8px 16px",
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "8px 18px",
                background: "var(--color-dark-bg, #27306f)",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              {saving ? "Registering..." : "Register"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal: View Member Details / Hotline Details */}
      {viewMember && (
        <Modal
          isOpen={true}
          onClose={() => setViewMember(null)}
          title={
            activeTab === "hotlines" || viewMember.category || viewMember.is_mandatory !== undefined
              ? `Campus Hotline: ${viewMember.title || "Hotline"}`
              : `Profile: ${viewMember.name || viewMember.full_name || "Member"}`
          }
        >
          {activeTab === "hotlines" || viewMember.category || viewMember.is_mandatory !== undefined ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "14px", paddingBottom: "12px", borderBottom: "1px solid #e2e8f0" }}>
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    background:
                      viewMember.category === "EMERGENCY"
                        ? "#fef2f2"
                        : viewMember.category === "LOGISTICS"
                        ? "#fef3c7"
                        : "#eff6ff",
                    color:
                      viewMember.category === "EMERGENCY"
                        ? "#dc2626"
                        : viewMember.category === "LOGISTICS"
                        ? "#b45309"
                        : "#2563eb",
                    fontSize: "22px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px solid #bfdbfe",
                  }}
                >
                  <PhoneCall size={24} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
                    {viewMember.title}
                  </h3>
                  <div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: "4px" }}>
                    <span
                      style={{
                        padding: "2px 8px",
                        background: viewMember.is_mandatory ? "#eff6ff" : "#f1f5f9",
                        color: viewMember.is_mandatory ? "#1d4ed8" : "#475569",
                        borderRadius: "6px",
                        fontSize: "12px",
                        fontWeight: "600",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      {viewMember.is_mandatory && <Lock size={11} />}
                      {viewMember.is_mandatory ? "Mandatory Campus Hotline" : "Custom Hotline"}
                    </span>
                    {viewMember.category && (
                      <span
                        style={{
                          padding: "2px 8px",
                          background: "#f8fafc",
                          color: "#64748b",
                          border: "1px solid #e2e8f0",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                        }}
                      >
                        {viewMember.category}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: "11.5px", color: "#64748b", display: "block" }}>Contact Phone</span>
                  <strong style={{ fontSize: "14px", color: "#0f172a" }}>{viewMember.contact_number || "Not Set"}</strong>
                </div>
                <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: "11.5px", color: "#64748b", display: "block" }}>Intercom Extension</span>
                  <strong style={{ fontSize: "14px", color: "#2563eb" }}>
                    {viewMember.extension ? `Ext ${viewMember.extension}` : "Direct Line Only"}
                  </strong>
                </div>
              </div>

              {viewMember.subtitle && (
                <div style={{ background: "#f8fafc", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: "11.5px", color: "#64748b", display: "block" }}>Department / Location Note</span>
                  <strong style={{ fontSize: "13px", color: "#334155" }}>{viewMember.subtitle}</strong>
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                <button
                  type="button"
                  onClick={() => setViewMember(null)}
                  style={{
                    padding: "8px 18px",
                    background: "#f1f5f9",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "600",
                    color: "#334155",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "14px", paddingBottom: "12px", borderBottom: "1px solid #e2e8f0" }}>
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                    color: "var(--color-dark-bg, #27306f)",
                    fontWeight: 800,
                    fontSize: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "2px solid #bfdbfe",
                  }}
                >
                  {(viewMember.name || viewMember.full_name || "U")[0]?.toUpperCase()}
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
                    {viewMember.name || viewMember.full_name}
                  </h3>
                  <span
                    style={{
                      display: "inline-block",
                      marginTop: "4px",
                      padding: "2px 8px",
                      background: "#f1f5f9",
                      color: "var(--color-dark-bg, #27306f)",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    {viewMember.role || viewMember.designation || "Colleague"}
                  </span>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: "11.5px", color: "#64748b", display: "block" }}>Department</span>
                  <strong style={{ fontSize: "13.5px", color: "#0f172a" }}>{viewMember.department || "General"}</strong>
                </div>
                <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: "11.5px", color: "#64748b", display: "block" }}>Floor</span>
                  <strong style={{ fontSize: "13.5px", color: "#0f172a" }}>{viewMember.floor ? `Floor ${viewMember.floor}` : "N/A"}</strong>
                </div>
                <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: "11.5px", color: "#64748b", display: "block" }}>Seating / Location</span>
                  <strong style={{ fontSize: "13.5px", color: "#0f172a" }}>
                    {viewMember.cabin_code && viewMember.desk_code
                      ? `Cabin ${viewMember.cabin_code} | Desk ${viewMember.desk_code}`
                      : viewMember.cabin_code
                      ? `Cabin ${viewMember.cabin_code}`
                      : viewMember.desk_code
                      ? `Desk ${viewMember.desk_code}`
                      : viewMember.unit || "General Workspace"}
                  </strong>
                </div>
                <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: "11.5px", color: "#64748b", display: "block" }}>Intercom Extension</span>
                  <strong style={{ fontSize: "13.5px", color: "#2563eb" }}>
                    {viewMember.extension || viewMember.ext ? `Ext ${viewMember.extension || viewMember.ext}` : "Not Assigned"}
                  </strong>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: "11.5px", color: "#64748b", display: "block" }}>Is Delegator</span>
                  <strong style={{ fontSize: "13px", color: viewMember.is_delegator !== false ? "#16a34a" : "#64748b" }}>
                    {viewMember.is_delegator !== false ? "✓ Yes (Can Delegate Approvals)" : "✕ No"}
                  </strong>
                </div>
                <div style={{ background: "#f8fafc", padding: "10px 12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: "11.5px", color: "#64748b", display: "block" }}>Is Delegatee</span>
                  <strong style={{ fontSize: "13px", color: viewMember.is_delegatee !== false ? "#16a34a" : "#64748b" }}>
                    {viewMember.is_delegatee !== false ? "✓ Yes (Can Receive Delegations)" : "✕ No"}
                  </strong>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "8px", background: "#f8fafc", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#334155" }}>
                  <Phone size={14} color="#64748b" />
                  <span>{viewMember.phone || viewMember.mobile_number || "No contact phone"}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#334155" }}>
                  <Mail size={14} color="#64748b" />
                  <span>{viewMember.email || viewMember.email_id || "No email address"}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#334155" }}>
                  <Shield size={14} color="#64748b" />
                  <span>Status: <strong style={{ color: viewMember.status === "Active" ? "#16a34a" : "#dc2626" }}>{viewMember.status || "Active"}</strong></span>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                <button
                  type="button"
                  onClick={() => setViewMember(null)}
                  style={{
                    padding: "8px 18px",
                    background: "#f1f5f9",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontWeight: "600",
                    color: "#334155",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Modal: Edit Employee / Colleague */}
      {editEmployee && (
        <Modal
          isOpen={true}
          onClose={() => setEditEmployee(null)}
          size="lg"
          title={`Edit Employee: ${editEmployee.name || "Member"}`}
        >
          <form onSubmit={handleSaveEmployeeEdit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ marginBottom: "-4px" }}>
              <FormField label="Full Name" required>
                <Input
                  type="text"
                  required
                  value={editEmployee.name || ""}
                  onChange={(e) => setEditEmployee({ ...editEmployee, name: e.target.value })}
                  placeholder="e.g. Rahul Sharma"
                  style={{ height: "38px", minHeight: "38px", padding: "8px 12px", borderRadius: "10px" }}
                />
              </FormField>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "-4px" }}>
              <FormField label="Phone Number">
                <Input
                  type="tel"
                  value={editEmployee.phone || ""}
                  onChange={(e) => setEditEmployee({ ...editEmployee, phone: e.target.value })}
                  placeholder="+91 9876543210"
                  style={{ height: "38px", minHeight: "38px", padding: "8px 12px", borderRadius: "10px" }}
                />
              </FormField>
              <FormField label="Work Email">
                <Input
                  type="email"
                  value={editEmployee.email || ""}
                  onChange={(e) => setEditEmployee({ ...editEmployee, email: e.target.value })}
                  placeholder="name@company.com"
                  style={{ height: "38px", minHeight: "38px", padding: "8px 12px", borderRadius: "10px" }}
                />
              </FormField>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "-4px" }}>
              <FormField label="Department" required>
                <Select
                  value={editEmployee.department || ""}
                  onChange={handleEditDeptChange}
                  placeholder="Select Department..."
                  options={editDepartmentOptions.map((d) => {
                    const dKey = d.name.toLowerCase();
                    const dFloors = formMeta.departmentFloors?.[dKey] || d.floors || [];
                    const floorSuffix = dFloors.length === 1
                      ? ` (${dFloors[0] === "0" ? "Ground" : `Floor ${dFloors[0]}`})`
                      : dFloors.length > 1
                        ? ` (${dFloors.map((fl) => fl === "0" ? "Ground" : `Fl ${fl}`).join(", ")})`
                        : "";
                    return {
                      value: d.name,
                      label: `${d.name}${floorSuffix}`,
                    };
                  })}
                />
              </FormField>
              <FormField label="Designation / Role" required>
                <Select
                  value={
                    editEmployee.is_custom_designation
                      ? "Other (Custom Designation)"
                      : editEmployee.designation || ""
                  }
                  onChange={(val) => {
                    const selected = typeof val === "object" ? val?.value : val;
                    if (selected === "Other (Custom Designation)") {
                      setEditEmployee((prev) => ({
                        ...prev,
                        designation: "",
                        is_custom_designation: true,
                      }));
                    } else {
                      setEditEmployee((prev) => ({
                        ...prev,
                        designation: selected,
                        is_custom_designation: false,
                      }));
                    }
                  }}
                  placeholder="Select Designation / Role..."
                  options={designationOptions}
                />
                {editEmployee.is_custom_designation && (
                  <Input
                    required
                    autoFocus
                    placeholder="Type custom designation / role..."
                    value={editEmployee.designation || ""}
                    onChange={(e) => setEditEmployee({ ...editEmployee, designation: e.target.value })}
                    style={{ marginTop: "6px", height: "36px", minHeight: "36px", padding: "6px 12px", borderRadius: "8px" }}
                  />
                )}
              </FormField>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "-4px" }}>
              <FormField label="Floor">
                <Select
                  disabled={editFloorConfig.isLocked}
                  value={String(editEmployee.floor || "")}
                  onChange={handleEditFloorChange}
                  placeholder="Select Floor..."
                  options={editFloorConfig.availableFloors.map((f) => ({
                    value: String(f.value),
                    label: f.label,
                  }))}
                />
                {editFloorConfig.isLocked && (
                  <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "3px", display: "flex", alignItems: "center", gap: "4px" }}>
                    <span>🔒</span>
                    <span>{editFloorConfig.lockReason}</span>
                  </div>
                )}
              </FormField>
              <FormField label="Reports To (HOD / Manager)">
                <Select
                  value={editEmployee.reports_to_user_id || ""}
                  onChange={(val) => {
                    const mgrVal = typeof val === "object" ? val?.value : val;
                    setEditEmployee({ ...editEmployee, reports_to_user_id: mgrVal });
                  }}
                  placeholder="Directly to Leadership / None"
                  isClearable
                  options={formMeta.managers.map((mgr) => ({
                    value: String(mgr.value),
                    label: mgr.label,
                  }))}
                />
              </FormField>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "-4px" }}>
              <FormField label="Seating Assignment Mode">
                <Select
                  value={editEmployee.seating_assignment_mode || "AUTO"}
                  onChange={(val) => {
                    const modeVal = typeof val === "object" ? val?.value : val;
                    if (modeVal === "EXISTING") {
                      setEditEmployee({
                        ...editEmployee,
                        seating_assignment_mode: "EXISTING",
                      });
                    } else {
                      autoGenerateEditCodes({
                        seating_assignment_mode: "AUTO",
                        selected_existing_seat: "",
                      });
                    }
                  }}
                  options={[
                    { value: "AUTO", label: "Generate New Space" },
                    { value: "EXISTING", label: "Assign Pre-existing Seat / Cabin" },
                  ]}
                />
              </FormField>

              {editEmployee.seating_assignment_mode === "EXISTING" ? (
                <FormField label="Select Existing Seat / Cabin" required>
                  <Select
                    value={editEmployee.selected_existing_seat || ""}
                    onChange={handleEditSelectExistingSeat}
                    placeholder="Choose existing desk, cabin, or cabin-desk..."
                    options={editExistingSeatOptions}
                  />
                </FormField>
              ) : (
                <FormField label="Space / Seating Type">
                  <Select
                    value={editEmployee.seating_type || "DESK"}
                    onChange={(val) => {
                      const typeVal = typeof val === "object" ? val?.value : val;
                      autoGenerateEditCodes({ seating_type: typeVal });
                    }}
                    options={[
                      { value: "DESK", label: "Dedicated Desk on Floor" },
                      { value: "CABIN", label: "Private Cabin (includes Cabin Desk)" },
                    ]}
                  />
                </FormField>
              )}
            </div>

            {/* Code Inputs: Desk Code (Auto-generated), Cabin Code, Intercom Ext. (Editable) */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "14px", alignItems: "start", marginTop: "2px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: "12.5px", fontWeight: "600", color: "#475569", lineHeight: "1.2", minHeight: "26px", display: "flex", alignItems: "flex-end" }}>
                  Desk Code (Auto-generated)
                </span>
                <Input
                  disabled
                  placeholder="Auto-generated"
                  value={editEmployee.desk_code || "Auto-generated"}
                  style={{ height: "38px", minHeight: "38px", padding: "8px 12px", borderRadius: "10px", background: "#f8fafc" }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: "12.5px", fontWeight: "600", color: "#475569", lineHeight: "1.2", minHeight: "26px", display: "flex", alignItems: "flex-end" }}>
                  Cabin Code {editEmployee.seating_type === "CABIN" || editEmployee.cabin_code ? "(Auto-generated)" : "(N/A)"}
                </span>
                <Input
                  disabled
                  placeholder={editEmployee.seating_type === "CABIN" || editEmployee.cabin_code ? "Auto-generated" : "N/A"}
                  value={
                    editEmployee.seating_type === "CABIN" || editEmployee.cabin_code
                      ? editEmployee.cabin_code || "Auto-generated"
                      : "N/A"
                  }
                  style={{ height: "38px", minHeight: "38px", padding: "8px 12px", borderRadius: "10px", background: "#f8fafc" }}
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <span style={{ fontSize: "12.5px", fontWeight: "600", color: "#334155", lineHeight: "1.2", minHeight: "26px", display: "flex", alignItems: "flex-end" }}>
                  Intercom Ext. (Editable)
                </span>
                <Input
                  type="text"
                  placeholder="e.g. 101"
                  value={editEmployee.extension || ""}
                  onChange={(e) => setEditEmployee({ ...editEmployee, extension: e.target.value })}
                  style={{ height: "38px", minHeight: "38px", padding: "8px 12px", borderRadius: "10px" }}
                />
              </div>
            </div>
            {/* Delegation Authority Settings: Is Delegator & Is Delegatee */}
            <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", padding: "12px 14px", marginTop: "2px" }}>
              <span style={{ fontSize: "12.5px", fontWeight: "700", color: "#334155", display: "block", marginBottom: "8px" }}>
                Visitor Approval Delegation Capabilities
              </span>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer", userSelect: "none" }}>
                  <input
                    type="checkbox"
                    checked={editEmployee.is_delegator !== false}
                    onChange={(e) => setEditEmployee({ ...editEmployee, is_delegator: e.target.checked })}
                    style={{ marginTop: "2px", accentColor: "var(--color-dark-bg, #27306f)", cursor: "pointer", width: "16px", height: "16px" }}
                  />
                  <span style={{ display: "flex", flexDirection: "column" }}>
                    <strong style={{ fontSize: "12.5px", color: "#1e293b" }}>Is Delegator</strong>
                    <span style={{ fontSize: "11px", color: "#64748b", lineHeight: 1.3 }}>
                      Can delegate own visitor approvals to eligible colleagues when away or busy.
                    </span>
                  </span>
                </label>

                <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer", userSelect: "none" }}>
                  <input
                    type="checkbox"
                    checked={editEmployee.is_delegatee !== false}
                    onChange={(e) => setEditEmployee({ ...editEmployee, is_delegatee: e.target.checked })}
                    style={{ marginTop: "2px", accentColor: "var(--color-dark-bg, #27306f)", cursor: "pointer", width: "16px", height: "16px" }}
                  />
                  <span style={{ display: "flex", flexDirection: "column" }}>
                    <strong style={{ fontSize: "12.5px", color: "#1e293b" }}>Is Delegatee</strong>
                    <span style={{ fontSize: "11px", color: "#64748b", lineHeight: 1.3 }}>
                      Appears in colleagues&apos; delegate list to receive and act on delegated visitor entries.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "14px" }}>
              <button
                type="button"
                onClick={() => setEditEmployee(null)}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: "8px 18px",
                  background: "var(--color-dark-bg, #27306f)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal: Custom Confirmation for Revoking Member Access */}
      {revokeTarget && (
        <Modal
          isOpen={true}
          onClose={() => setRevokeTarget(null)}
          title="Confirm Revocation"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  background: "#fee2e2",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <AlertTriangle size={24} />
              </div>
              <div>
                <h4 style={{ margin: "0 0 6px 0", fontSize: "15px", fontWeight: "700", color: "#0f172a" }}>
                  Revoke access for {revokeTarget.name}?
                </h4>
                <p style={{ margin: 0, fontSize: "13px", color: "#64748b", lineHeight: 1.5 }}>
                  {revokeTarget.type === "management"
                    ? `This will revoke the leadership/committee role (${revokeTarget.role}) for this member. They will no longer hold administrative management status.`
                    : `This will deactivate and remove this member from the active property directory, revoke assigned units/desks, and disable their gate check-in pass privileges.`}
                </p>
              </div>
            </div>

            {revokeTarget.type !== "management" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <p style={{ margin: 0, fontSize: "13px", color: "#334155" }}>
                  To confirm deactivation and soft-deletion, please type the assigned desk or cabin code:{" "}
                  <strong style={{ fontFamily: "monospace", color: "#0f172a", background: "#f1f5f9", padding: "2px 6px", borderRadius: "4px" }}>
                    {revokeTarget.expectedDeskCode || "N/A"}
                  </strong>
                </p>
                <FormField
                  label="Enter Desk / Cabin Code to Confirm"
                  required
                  error={revokeError}
                >
                  <Input
                    type="text"
                    value={revokeDeskCodeInput}
                    onChange={(e) => {
                      setRevokeDeskCodeInput(e.target.value);
                      if (revokeError) setRevokeError("");
                    }}
                    placeholder={`e.g. ${revokeTarget.expectedDeskCode || "DESK-CODE"}`}
                    autoFocus
                  />
                </FormField>
              </div>
            )}

            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "6px",
                padding: "10px 12px",
                fontSize: "12.5px",
                color: "#991b1b",
              }}
            >
              ⚠️ This will soft-delete the employee profile and deactivate user login access. All records and history will be securely retained in the database.
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "10px" }}>
              <button
                type="button"
                onClick={() => {
                  setRevokeTarget(null);
                  setRevokeDeskCodeInput("");
                  setRevokeError("");
                }}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  saving ||
                  (revokeTarget.type !== "management" &&
                    revokeTarget.expectedDeskCode &&
                    !revokeDeskCodeInput.trim())
                }
                onClick={handleConfirmRevoke}
                style={{
                  padding: "8px 18px",
                  background:
                    saving ||
                    (revokeTarget.type !== "management" &&
                      revokeTarget.expectedDeskCode &&
                      !revokeDeskCodeInput.trim())
                      ? "#fca5a5"
                      : "#dc2626",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor:
                    saving ||
                    (revokeTarget.type !== "management" &&
                      revokeTarget.expectedDeskCode &&
                      !revokeDeskCodeInput.trim())
                      ? "not-allowed"
                      : "pointer",
                  fontWeight: "600",
                }}
              >
                {saving ? "Revoking..." : "Confirm Revoke"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: Add or Edit Campus Hotline */}
      {isHotlineModalOpen && (
        <Modal
          isOpen={isHotlineModalOpen}
          onClose={() => setIsHotlineModalOpen(false)}
          title={
            hotlineModalMode === "add"
              ? "Add Custom Campus Hotline"
              : `Edit Hotline: ${hotlineForm.title}`
          }
        >
          <form onSubmit={handleSaveHotline} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {hotlineForm.is_mandatory && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  fontSize: "12.5px",
                  color: "#1e40af",
                }}
              >
                <Lock size={15} color="#1d4ed8" />
                <span>
                  <strong>Mandatory Campus Hotline:</strong> The name is standard across all properties, but you can update its phone number, extension, and subtitle.
                </span>
              </div>
            )}

            <div>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px", color: "#334155" }}>
                Hotline Name / Designation <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="text"
                required
                disabled={hotlineForm.is_mandatory}
                placeholder="e.g. Server Room Support, Dispatch Desk"
                value={hotlineForm.title}
                onChange={(e) => setHotlineForm({ ...hotlineForm, title: e.target.value })}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                  background: hotlineForm.is_mandatory ? "#f1f5f9" : "#fff",
                  color: hotlineForm.is_mandatory ? "#64748b" : "#0f172a",
                  cursor: hotlineForm.is_mandatory ? "not-allowed" : "text",
                }}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px", color: "#334155" }}>
                  Contact Phone Number <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="+91 98201 10101"
                  value={hotlineForm.contact_number}
                  onChange={(e) => setHotlineForm({ ...hotlineForm, contact_number: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px", color: "#334155" }}>
                  Intercom Extension
                </label>
                <input
                  type="text"
                  placeholder="e.g. 101, 911"
                  value={hotlineForm.extension}
                  onChange={(e) => setHotlineForm({ ...hotlineForm, extension: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.8fr", gap: "12px" }}>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px", color: "#334155" }}>
                  Subtitle / Department Note
                </label>
                <input
                  type="text"
                  placeholder="e.g. Receptionist • Lobby, Security Post • Gate 1"
                  value={hotlineForm.subtitle}
                  onChange={(e) => setHotlineForm({ ...hotlineForm, subtitle: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: "600", marginBottom: "6px", color: "#334155" }}>
                  Category
                </label>
                <select
                  value={hotlineForm.category}
                  onChange={(e) => setHotlineForm({ ...hotlineForm, category: e.target.value })}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "6px",
                    fontSize: "13.5px",
                    boxSizing: "border-box",
                    background: "#fff",
                  }}
                >
                  <option value="GENERAL">General</option>
                  <option value="RECEPTION">Reception / Front Desk</option>
                  <option value="SECURITY">Security / Gate</option>
                  <option value="LOGISTICS">Logistics / Dock</option>
                  <option value="EMERGENCY">Emergency Response</option>
                </select>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "12px" }}>
              <button
                type="button"
                onClick={() => setIsHotlineModalOpen(false)}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: "8px 18px",
                  background: "var(--color-dark-bg, #27306f)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: saving ? "not-allowed" : "pointer",
                  fontWeight: "600",
                }}
              >
                {saving ? "Saving..." : hotlineModalMode === "add" ? "Add Hotline" : "Save Changes"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal: Delete Custom Hotline Confirmation */}
      {hotlineDeleteTarget && (
        <Modal
          isOpen={true}
          onClose={() => setHotlineDeleteTarget(null)}
          title="Delete Custom Hotline"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  background: "#fef2f2",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Trash2 size={22} />
              </div>
              <div>
                <p style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#0f172a" }}>
                  Are you sure you want to delete the hotline &quot;{hotlineDeleteTarget.title}&quot;?
                </p>
                <p style={{ margin: "4px 0 0 0", fontSize: "12.5px", color: "#64748b" }}>
                  Extension: {hotlineDeleteTarget.extension || "N/A"} • Number: {hotlineDeleteTarget.contact_number}
                </p>
              </div>
            </div>

            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "6px",
                padding: "10px 12px",
                fontSize: "12.5px",
                color: "#991b1b",
              }}
            >
              ⚠️ This will remove the hotline contact from the directory and mobile quick-dial cards.
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "8px" }}>
              <button
                type="button"
                onClick={() => setHotlineDeleteTarget(null)}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #cbd5e1",
                  background: "#f8fafc",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleConfirmDeleteHotline}
                style={{
                  padding: "8px 18px",
                  background: saving ? "#fca5a5" : "#dc2626",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: saving ? "not-allowed" : "pointer",
                  fontWeight: "600",
                }}
              >
                {saving ? "Deleting..." : "Delete Hotline"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PropertyDirectories;
